import { memo, useMemo } from 'react';
import { motion } from 'framer-motion';

interface BuddyAuraProps {
  lightningVitality?: number;
}

/**
 * BuddyAura — minimal warm backlight behind Buddy.
 * When lightningVitality > 0.5, tiny ⚡ bolts orbit the aura.
 */
function BuddyAuraInner({ lightningVitality = 0.5 }: BuddyAuraProps) {
  const baseSize = 200;

  const showBolts = lightningVitality > 0.5;
  const boltCount = showBolts ? Math.round(lightningVitality * 3) : 0;
  const boltOpacity = showBolts ? (lightningVitality - 0.5) * 0.6 : 0;
  const shouldArc = lightningVitality > 0.8;

  const bolts = useMemo(() =>
    Array.from({ length: 3 }, (_, i) => ({
      id: i,
      offsetDeg: i * 120,
    })),
  []);

  return (
    <>
      {/* Base aura glow */}
      <motion.div
        className="absolute top-1/2 left-1/2 rounded-full pointer-events-none z-0"
        style={{
          width: baseSize,
          height: baseSize * 1.1,
          x: '-50%',
          y: '-50%',
          background: `radial-gradient(ellipse at 50% 55%, hsl(40 50% 70% / 0.06) 0%, hsl(40 40% 60% / 0.02) 50%, transparent 75%)`,
          filter: 'blur(30px)',
        }}
        animate={{
          opacity: [0.6, 0.75, 0.6],
        }}
        transition={{
          duration: 8,
          repeat: Infinity,
          ease: 'easeInOut',
        }}
      />

      {/* Lightning bolts orbiting aura */}
      {boltCount > 0 && bolts.slice(0, boltCount).map(bolt => (
        <motion.div
          key={`bolt-${bolt.id}`}
          className="absolute top-1/2 left-1/2 pointer-events-none z-0"
          style={{
            width: 0,
            height: 0,
          }}
          animate={{
            rotate: [bolt.offsetDeg, bolt.offsetDeg + 360],
          }}
          transition={{
            duration: 12,
            repeat: Infinity,
            ease: 'linear',
          }}
        >
          <motion.span
            className="absolute select-none"
            style={{
              left: baseSize / 2 + 8,
              top: -5,
              fontSize: 10,
              color: 'hsl(var(--bitcoin) / 0.5)',
              filter: 'drop-shadow(0 0 2px hsl(var(--bitcoin) / 0.2))',
            }}
            animate={shouldArc ? {
              opacity: [boltOpacity, boltOpacity, 0.9, boltOpacity, boltOpacity],
            } : {
              opacity: boltOpacity,
            }}
            transition={shouldArc ? {
              duration: 4 + bolt.id * 1.5,
              repeat: Infinity,
              ease: 'easeInOut',
            } : {
              duration: 2,
            }}
          >
            ⚡
          </motion.span>
        </motion.div>
      ))}
    </>
  );
}

const BuddyAura = memo(BuddyAuraInner);
export default BuddyAura;
